import React, { useContext, useCallback } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { PapiGallection } from '@wix/da-types-papi';
import { MobileContext } from '@wix/da-shared-react/pkg/Context/MobileContext';
import { useEffectOnceOnMount } from '@wix/da-shared-react/pkg/utils/hooks/useEffectOnceOnMount';
import DesktopGallectionsListView from './DesktopGallectionsListView';
import MobileGallectionsListView from './MobileGallectionsListView';
import { useNonEmptyGallectionFolders } from '../hooks/useGallectionFolders';

const DISTANCE_TO_RENDER_NEXT_FOLDER = 2; // 2 window heights

export type Props = {
  currentFolder?: PapiGallection;
  nextFullViewFolderId?: number;
  setCurrentlyViewedFolderId: (folderId: number) => void;
  addFolderToFullViewList: (folderId: number) => void;
};

const GallectionsListView: React.FC<Props &
  RouteComponentProps<{ folderId?: string }>> = ({
  currentFolder,
  match: {
    params: { folderId },
  },
  addFolderToFullViewList,
  setCurrentlyViewedFolderId,
  nextFullViewFolderId,
}) => {
  const isMobile = useContext(MobileContext);
  const { folders, hasMore, fetchNext } = useNonEmptyGallectionFolders();

  useEffectOnceOnMount(() => {
    if (!folderId) {
      return;
    }

    const intFolderId = +folderId;
    const parentId = folders.find(item => item.folderId === intFolderId)
      ?.parentId;
    const anchor = document.getElementById('sub-folder-gallery');

    if (!parentId || !anchor) {
      return;
    }

    anchor.scrollIntoView();
  });

  const onScrollToFolder = useCallback(
    (scrollToFolderId: number) => {
      if (scrollToFolderId !== currentFolder?.folderId) {
        setCurrentlyViewedFolderId(scrollToFolderId);
      }
    },
    [currentFolder, setCurrentlyViewedFolderId]
  );

  const onScrolledToBottom = useCallback(() => {
    if (nextFullViewFolderId) {
      addFolderToFullViewList(nextFullViewFolderId);
      return;
    }
    if (hasMore) {
      fetchNext();
    }
  }, [nextFullViewFolderId, addFolderToFullViewList, hasMore, fetchNext]);

  if (isMobile) {
    return (
      <MobileGallectionsListView
        distanceToRender={DISTANCE_TO_RENDER_NEXT_FOLDER}
        currentFolder={currentFolder}
        onScrollToFolder={onScrollToFolder}
        onScrolledToBottom={onScrolledToBottom}
      />
    );
  }
  return (
    <DesktopGallectionsListView
      distanceToBottom={DISTANCE_TO_RENDER_NEXT_FOLDER}
      onScrollToFolder={onScrollToFolder}
      onScrolledToBottom={onScrolledToBottom}
    />
  );
};
GallectionsListView.displayName = 'GallectionsListView';

export default withRouter(GallectionsListView) as React.ComponentType<Props>;
